import { useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, Check, MapPin, Building2, Briefcase, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ConfidenceDots } from "../ConfidenceDots";
import { ENTITY_TYPE_LABELS } from "../types";
import type { ManualSearchCandidate, WizardState } from "../types";

interface ManualEntityResolvePanelProps {
  status: WizardState["manualSearchStatus"];
  candidates: ManualSearchCandidate[];
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  escapeHatch: boolean;
  onEscapeHatch: () => void;
  questions: string[];
  onAnswerClarification: (answer: string) => void;
  error: string | null;
  /** Clears the search and hands focus back to the description textarea. */
  onEditDescription: () => void;
  /** Re-runs the search asking the model for other/different candidates. */
  onSearchMore: () => void;
}

/**
 * Renders below the manual-description textarea in Step1Extract once a search has
 * run (PROJECT-077). Single-select candidate picker — unlike Step4Review's
 * accept/reject checklist, this resolves ONE identity, not a list of sub-entities.
 */
export function ManualEntityResolvePanel({
  status,
  candidates,
  selectedIndex,
  onSelect,
  escapeHatch,
  onEscapeHatch,
  questions,
  onAnswerClarification,
  error,
  onEditDescription,
  onSearchMore,
}: ManualEntityResolvePanelProps) {
  const [clarificationAnswer, setClarificationAnswer] = useState("");

  if (status === "idle") return null;

  if (status === "searching") {
    return (
      <div className="flex items-center gap-2 text-md text-muted-foreground/70 py-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Searching the web…
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 space-y-3">
        <p className="text-lg text-destructive">{error ?? "Search failed — please try again."}</p>
        <EscapeHatchButton active={escapeHatch} onClick={onEscapeHatch} />
      </div>
    );
  }

  if (status === "ambiguous") {
    const handleSearchAgain = () => {
      if (!clarificationAnswer.trim()) return;
      onAnswerClarification(clarificationAnswer.trim());
      setClarificationAnswer("");
    };
    return (
      <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.05] p-4 space-y-3">
        <div className="flex items-start gap-2.5">
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-lg text-foreground leading-snug">{questions[0]}</p>
        </div>
        <div className="flex gap-2">
          <Input
            autoFocus
            value={clarificationAnswer}
            onChange={(e) => setClarificationAnswer(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearchAgain()}
            placeholder="Type your answer…"
            className="h-9 text-lg"
          />
          <Button
            size="sm"
            className="h-9 text-md bg-amber-500 hover:bg-amber-400 text-white shrink-0"
            disabled={!clarificationAnswer.trim()}
            onClick={handleSearchAgain}
          >
            Search again
          </Button>
        </div>
        <EscapeHatchButton active={escapeHatch} onClick={onEscapeHatch} />
      </div>
    );
  }

  // status === "resolved" — one detail card per candidate (name, type, affiliation,
  // location, evidence, confidence) rather than a single-line row, so the user has enough
  // to actually tell two similarly-named real-world matches apart before picking.
  return (
    <div className="flex flex-col gap-2.5">
      {candidates.map((candidate, index) => {
        const selected = selectedIndex === index;
        return (
          <button
            key={`${candidate.entity_name}-${index}`}
            type="button"
            onClick={() => onSelect(index)}
            className={cn(
              "flex flex-col gap-1.5 rounded-lg border px-3.5 py-3 text-left transition-all",
              selected
                ? "border-blue-500/40 bg-blue-500/[0.07]"
                : "border-border/40 hover:border-border/70",
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className={cn(
                    "h-3.5 w-3.5 rounded border shrink-0 flex items-center justify-center",
                    selected ? "bg-blue-600 border-blue-600" : "border-border/60",
                  )}
                >
                  {selected && <Check className="h-2 w-2 stroke-[3] text-white" />}
                </div>
                <p className="text-lg font-medium text-foreground truncate">{candidate.entity_name}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Badge variant="outline" className="text-label border-border/50 text-muted-foreground">
                  {ENTITY_TYPE_LABELS[candidate.entity_type] ?? candidate.entity_type}
                </Badge>
                <ConfidenceDots filled={Math.max(1, Math.min(5, Math.round(candidate.confidence_score * 5)))} />
              </div>
            </div>

            {(candidate.role || candidate.affiliation || candidate.location) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-6 text-sm text-muted-foreground/70">
                {candidate.role && (
                  <span className="flex items-center gap-1">
                    <Briefcase className="h-3 w-3 shrink-0" />
                    {candidate.role}
                  </span>
                )}
                {candidate.affiliation && (
                  <span className="flex items-center gap-1">
                    <Building2 className="h-3 w-3 shrink-0" />
                    {candidate.affiliation}
                  </span>
                )}
                {candidate.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3 shrink-0" />
                    {candidate.location}
                  </span>
                )}
              </div>
            )}

            {candidate.evidence && (
              <p className="pl-6 text-sm text-subtle-foreground leading-relaxed">{candidate.evidence}</p>
            )}

            {candidate.existing_matches.length > 0 && (
              <div className="pl-6">
                <Badge variant="outline" className="text-label border-blue-500/30 text-blue-400">
                  {candidate.existing_matches.length === 1
                    ? "Possible match found"
                    : `${candidate.existing_matches.length} possible matches`}
                </Badge>
              </div>
            )}

            {candidate.dedup_check_skipped && (
              <div className="pl-6">
                <Badge
                  variant="outline"
                  className="text-label border-amber-500/30 text-amber-400 gap-1"
                >
                  <ShieldAlert className="h-2.5 w-2.5" />
                  Couldn't check for duplicates — verify manually
                </Badge>
              </div>
            )}
          </button>
        );
      })}
      <CandidateFallbackActions
        escapeHatch={escapeHatch}
        onEscapeHatch={onEscapeHatch}
        onEditDescription={onEditDescription}
        onSearchMore={onSearchMore}
      />
    </div>
  );
}

/** Shown under the candidate list — three distinct escapes instead of one ambiguous
 * "none of these" button: rewrite the description, keep it as typed, or ask the search
 * for a different set of matches. */
function CandidateFallbackActions({
  escapeHatch,
  onEscapeHatch,
  onEditDescription,
  onSearchMore,
}: {
  escapeHatch: boolean;
  onEscapeHatch: () => void;
  onEditDescription: () => void;
  onSearchMore: () => void;
}) {
  return (
    <div className="flex flex-col gap-1.5 pt-1.5 mt-0.5 border-t border-border/30">
      <p className="text-sm text-subtle-foreground pt-2">None of these look right?</p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <button
          type="button"
          onClick={onEditDescription}
          className="text-md text-muted-foreground/70 hover:text-foreground transition-colors"
        >
          Edit my description
        </button>
        <button
          type="button"
          onClick={onSearchMore}
          className="text-md text-muted-foreground/70 hover:text-foreground transition-colors"
        >
          Search for more options
        </button>
        <button
          type="button"
          onClick={onEscapeHatch}
          className={cn(
            "flex items-center gap-1.5 text-md transition-colors",
            escapeHatch ? "text-blue-400 font-medium" : "text-muted-foreground/70 hover:text-foreground",
          )}
        >
          {escapeHatch && <CheckCircle2 className="h-3.5 w-3.5" />}
          Use my description as-is
        </button>
      </div>
    </div>
  );
}

function EscapeHatchButton({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 text-md self-start transition-colors",
        active ? "text-blue-400 font-medium" : "text-subtle-foreground hover:text-muted-foreground",
      )}
    >
      {active && <CheckCircle2 className="h-3.5 w-3.5" />}
      Use my description as-is
    </button>
  );
}
